import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocomplete } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipListChange, MatChipRemove } from '@angular/material/chips';
import { of, Subject } from 'rxjs';
import { debounceTime, map, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

/**
 * Autocomplete Angular Material chips input
 */
@Component({
	selector: 'app-autocomplete',
	templateUrl: './autocomplete.component.html',
	styleUrls: ['./autocomplete.component.scss'],
	changeDetection: ChangeDetectionStrategy.Default
})
export class AutocompleteComponent implements OnInit, OnDestroy {
	@Input() label;
	@Input() visible = true;
	@Input() selectable = true;
	@Input() removable = true;
	@Input() separatorKeysCodes: number[] = [ENTER, COMMA];
	@Input() allItems: any[] = [];
	@Input() items: any[] = [];
	@Input() formGroup: UntypedFormGroup;
	@Input() controlName: string;
	@Input() canAddNew = false;
	@Input() addDescription = 'Add an item...';
	@Input() filteredItemsAPI: () => void;
	@Input() floatLabel = 'auto';
	@Input() tooltip: string;
	@Input() defaultValues: any[];

	@Input()
	public get filteredItems() {
		return this._filteredItems;
	}
	public set filteredItems(newValue) {
		this._filteredItems = newValue?.filter(entity => !this.formGroup.get(this.controlName)?.value?.find(e => e.id === entity.id));
	}

	// tslint:disable-next-line: variable-name
	private _filteredItems: any[];

	@Output() valueChange: EventEmitter<any> = new EventEmitter();
	@Output() typing: EventEmitter<string> = new EventEmitter();
	@Output() changed: EventEmitter<MatChipListChange> = new EventEmitter();
	@Output() added: EventEmitter<MatChipRemove> = new EventEmitter();
	@Output() removed: EventEmitter<MatChipRemove> = new EventEmitter();

	@ViewChild('itemInput') itemInput: ElementRef<HTMLInputElement>;
	@ViewChild('auto') matAutocomplete: MatAutocomplete;

	public itemCtrl = new UntypedFormControl();
	public currentQuery = '';

	private unsubscribe$: Subject<void> = new Subject();

	constructor() {}

	ngOnInit() {
		if (this.formGroup) {
			// Emit value changes to parent
			// Hack: Had to add a timeout, because this was firing before the value was really set?
			// So the api call was a step behind from the state.
			this.formGroup
				.get(this.controlName)
				.valueChanges.pipe(takeUntil(this.unsubscribe$))
				.subscribe(value => setTimeout(() => this.valueChange.emit(value), 100));

			// Filter our autocomplete to not include previously added values
			this.itemCtrl.valueChanges
				.pipe(
					// startWith(this.currentQuery),
					takeUntil(this.unsubscribe$),
					debounceTime(400),
					map(text => {
						// This can be a string being typed, or the group of objects that are in the input
						// Its weird. But we can just check and return suggestions when its a string.
						if (typeof text === 'string') {
							this.getFilteredItems(text);
						} else {
							return of([]);
						}
					})
				)
				.subscribe();
      if (this.defaultValues) {
        this.formGroup.controls[this.controlName].setValue([...this.defaultValues]);
      }
		} else {
			console.warn('Couldnt find a FormGroup Input', this.controlName);
		}
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	/**
	 * Add an item to the form field if that is allowed
	 * @param event
	 */
	add(event: MatChipInputEvent): void {
		const input = event.input;
		const value = event.value;

		if (this.canAddNew) {
			const items = [...this.formGroup.value[this.controlName]];

			// Add our item
			items.push({
				id: uuidv4(),
				name: value
			});

			this.formGroup.patchValue({ [this.controlName]: items });
		}

		// Reset the input value
		if (input) {
			input.value = '';
		}

		this.added.emit();
	}

	/**
	 * Remove an item from the value's array
	 * @param item
	 */
	remove(item): void {
		const items = [...this.formGroup.value[this.controlName]];
		const index = items.findIndex(i => i.id === item.id);

		if (index >= 0) {
			items.splice(index, 1);
		}

		this.formGroup.patchValue({ [this.controlName]: items });
		this.removed.emit(item);
	}

	/**
	 * When the user selects a value, add it to the form value
	 * @param event
	 */
	selected(event: MatAutocompleteSelectedEvent): void {
		this.itemInput.nativeElement.value = '';
		const initialValue = this.formGroup.value[this.controlName] || [];

		this.formGroup.patchValue({
			[this.controlName]: [...initialValue, event.option.value]
		});
		this.changed.emit(event.option.value);
	}

	/**
	 * Returns items that match the query string passed in
	 * @param text
	 */
	getFilteredItems(text: string) {
		this.currentQuery = text;

		if (this.allItems?.length) {
			this.filteredItems = this.allItems?.filter(item => {
				// Don't return anything that already exists in this value
				if (this.formGroup.get(this.controlName)?.value?.find(i => i.id === item.id)) {
					return false;
				}

				// Don't return that doesnt match any text being typed
				return item.name?.toLowerCase().indexOf(text?.toLowerCase()) > -1;
			});
		} else {
			this.typing.emit(text);
		}
	}

	displayWithName(item) {
		return item?.name;
	}
}
